import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Flame, X } from "lucide-react";
import { submitHypePulse } from "@/lib/hype.functions";
import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { useAdaptiveFanProfile } from "@/lib/adaptiveFan/adaptiveFanProfile";
import { useFanPoints } from "@/hooks/useFanPoints";
import { HeatRivalryMini } from "@/components/social/HeatRivalryMini";
import { useSelectedNation } from "@/hooks/useSelectedNation";

const FLAGS: Record<string, string> = {
  MEX: "🇲🇽", ARG: "🇦🇷", BRA: "🇧🇷", FRA: "🇫🇷", ESP: "🇪🇸", USA: "🇺🇸",
  ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹", URU: "🇺🇾", COL: "🇨🇴",
  JPN: "🇯🇵", KOR: "🇰🇷", NED: "🇳🇱", BEL: "🇧🇪", CRO: "🇭🇷", MAR: "🇲🇦",
};
const NAMES: Record<string, string> = {
  MEX: "México", ARG: "Argentina", BRA: "Brasil", FRA: "Francia", ESP: "España",
  USA: "USA", ENG: "Inglaterra", GER: "Alemania", ITA: "Italia", POR: "Portugal",
  URU: "Uruguay", COL: "Colombia", JPN: "Japón", KOR: "Corea", NED: "Países Bajos",
  BEL: "Bélgica", CRO: "Croacia", MAR: "Marruecos",
};
const FIRST_USE_KEY = "vs_fan_power_first_seen_v1";

interface HypeMeterButtonProps {
  matchId: string;
  homeCode?: string | null;
  awayCode?: string | null;
  homeTeamName?: string | null;
  awayTeamName?: string | null;
}

export function HypeMeterButton({ matchId, homeCode, awayCode, homeTeamName, awayTeamName }: HypeMeterButtonProps) {
  const { t } = useLanguage();
  // Unique per-instance suffix — prevents Supabase channel topic collisions
  // if HypeMeterButton is ever mounted twice with the same matchId.
  const instanceId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const profile = useAdaptiveFanProfile();
  const { nation: selectedNation } = useSelectedNation();
  const { current: fanLevel, next: fanNext, progress: fanProgress, points: fanPoints } = useFanPoints();
  const [localBurst, setLocalBurst] = useState(0);
  const [globalIntensity, setGlobalIntensity] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [firstUseOpen, setFirstUseOpen] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [pulseConfirm, setPulseConfirm] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const dragStartRef = useRef<number | null>(null);
  const bufferRef = useRef(0);
  const rollingRef = useRef<{ t: number; n: number }[]>([]);
  const confirmTimerRef = useRef<number | undefined>(undefined);
  const cooldownTimerRef = useRef<number | undefined>(undefined);

  // P0 — Fan Support must ALWAYS follow the user's selected nation
  // (canonical: profiles.selection_team via useSelectedNation, then the
  // adaptive fan profile favoriteTeam). Only if the user has picked
  // nothing do we fall back to the match's home/away code.
  const supportedCode = (
    selectedNation || profile.favoriteTeam || homeCode || awayCode || ""
  ).toUpperCase();
  const supportedName =
    NAMES[supportedCode] ??
    (supportedCode === (homeCode ?? "").toUpperCase()
      ? (homeTeamName || homeCode || supportedCode)
      : supportedCode === (awayCode ?? "").toUpperCase()
        ? (awayTeamName || awayCode || supportedCode)
        : supportedCode);
  const supportedFlag = FLAGS[supportedCode] ?? "🏳️";

  useEffect(() => {
    const id = window.setInterval(() => {
      const count = bufferRef.current;
      if (count <= 0) return;
      bufferRef.current = 0;
      void submitHypePulse({
        data: { match_id: matchId, session_id: getSessionId(), pulse_type: "fire", pulse_count: Math.min(50, count) },
      }).catch(() => {});
    }, 500);
    return () => window.clearInterval(id);
  }, [matchId]);

  useEffect(() => {
    const channel = supabase
      .channel(`hype:${matchId}:${instanceId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "hype_pulses", filter: `match_id=eq.${matchId}` }, (payload) => {
        const n = (payload.new as { pulse_count?: number })?.pulse_count ?? 1;
        const now = Date.now();
        rollingRef.current = [...rollingRef.current.filter((e) => now - e.t < 5000), { t: now, n }];
        const total = rollingRef.current.reduce((acc, e) => acc + e.n, 0);
        setGlobalIntensity(Math.min(1, total / 200));
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [matchId, instanceId]);

  const actuallySendPulse = useCallback(() => {
    if (cooldown > 0) return;
    bufferRef.current += 1;
    setLocalBurst((b) => b + 1);
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      try { navigator.vibrate?.(8); } catch { /* noop */ }
    }
    window.setTimeout(() => setLocalBurst((b) => Math.max(0, b - 1)), 600);
    setPulseConfirm(true);
    if (confirmTimerRef.current) window.clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = window.setTimeout(() => setPulseConfirm(false), 4500);
    setCooldown(12);
    if (cooldownTimerRef.current) window.clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = window.setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (cooldownTimerRef.current) window.clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    void trackEvent("fan_power_pulse_sent", { team: supportedCode });
  }, [cooldown, supportedCode]);

  const sendPulse = useCallback(() => {
    if (cooldown > 0) return;
    if (typeof window !== "undefined") {
      const seen = window.localStorage.getItem(FIRST_USE_KEY);
      if (!seen) {
        window.localStorage.setItem(FIRST_USE_KEY, "1");
        setFirstUseOpen(true);
        void trackEvent("fan_power_first_use_shown", {});
        return;
      }
    }
    actuallySendPulse();
  }, [cooldown, actuallySendPulse]);

  const dismissFirstUse = useCallback(() => {
    setFirstUseOpen(false);
    actuallySendPulse();
  }, [actuallySendPulse]);

  useEffect(() => () => {
    if (confirmTimerRef.current) window.clearTimeout(confirmTimerRef.current);
    if (cooldownTimerRef.current) window.clearInterval(cooldownTimerRef.current);
  }, []);

  const heat = Math.max(globalIntensity, Math.min(1, localBurst / 10));
  const scale = 1 + heat * 0.25;
  const hasActiveWindow = heat > 0.05 || pulseConfirm;
  const momentumPct = Math.round(globalIntensity * 100);
  const pressurePct = Math.round(Math.min(1, heat + 0.1) * 100);
  const intensityPct = Math.round(heat * 100);
  const boostLabel = t("heat_panel_boost").replace("{team}", supportedName);
  const impactLabel = t("heat_panel_impact")
    .replace("{flag}", supportedFlag)
    .replace("{team}", supportedName);

  const openPanel = () => {
    setPanelOpen(true);
    void trackEvent("advanced_stats_tooltip_opened", { metric: "heat_panel" });
  };

  return (
    <>
    <div className="pointer-events-auto absolute bottom-14 right-3 z-30 flex flex-col items-end gap-1">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary/60">
        <div className="heat-gradient-team h-full rounded-full transition-all duration-150"
          style={{ width: `${Math.round(heat * 100)}%` }}
          aria-label={t("hype_meter_intensity")} role="progressbar" aria-valuenow={Math.round(heat * 100)} aria-valuemin={0} aria-valuemax={100} />
      </div>
      <button type="button" onClick={openPanel}
        aria-label={`${supportedFlag} ${t("fan_power_button")} · ${supportedName}`}
        title={`${supportedFlag} ${t("fan_power_button")} · ${supportedName}`}
        className="border-team-overlay shadow-team-identity flex min-h-[48px] items-center gap-1.5 rounded-2xl border bg-background/85 px-2.5 py-1.5 backdrop-blur transition-transform active:scale-90"
        style={{ transform: `scale(${scale})` }}>
        <span aria-hidden className="text-base leading-none">{supportedFlag}</span>
        <span className="text-[10px] font-black uppercase tracking-wider leading-none text-foreground">
          {t("fan_power_button")}
        </span>
        <Flame className="text-team-heat h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
    {panelOpen && (
      <div
        className="fixed inset-0 z-[55] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
        onClick={() => setPanelOpen(false)}
        role="dialog"
        aria-label={t("heat_panel_title")}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-t-3xl border border-border/60 bg-background p-4 shadow-2xl sm:rounded-2xl transition-transform"
          style={{ transform: dragY > 0 ? `translateY(${dragY}px)` : undefined }}
          onTouchStart={(e) => { dragStartRef.current = e.touches[0].clientY; }}
          onTouchMove={(e) => {
            if (dragStartRef.current === null) return;
            const dy = e.touches[0].clientY - dragStartRef.current;
            if (dy > 0) setDragY(Math.min(dy, 280));
          }}
          onTouchEnd={() => {
            if (dragY > 110) setPanelOpen(false);
            setDragY(0);
            dragStartRef.current = null;
          }}
        >
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden" aria-hidden />
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span aria-hidden className="text-base leading-none">{supportedFlag}</span>
              <h3 className="text-sm font-black">{t("heat_panel_title")} · {supportedName}</h3>
            </div>
            <button
              onClick={() => setPanelOpen(false)}
              aria-label={t("insights_pro_close")}
              className="rounded-full border border-border/60 p-1.5 text-muted-foreground hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {hasActiveWindow ? (
            <div className="space-y-2.5">
              <HeatBar label={t("heat_panel_momentum")} value={momentumPct} />
              <HeatBar label={t("heat_panel_pressure")} value={pressurePct} />
              <HeatBar label={t("heat_panel_intensity")} value={intensityPct} />
              {pulseConfirm && (
                <div className="animate-in fade-in slide-in-from-bottom-1 rounded-xl border border-primary/40 bg-primary/10 p-3 text-center">
                  <div className="text-sm font-black text-primary">{t("heat_panel_pulse_sent")}</div>
                  <div className="mt-0.5 text-[12px] font-bold text-foreground/90">{impactLabel}</div>
                  <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{t("heat_panel_reward")}</div>
                </div>
              )}
              <HeatRivalryMini highlight={supportedCode} />
              <HeatContributions t={t} />
              <FanLevelStrip
                level={fanLevel.label}
                emoji={fanLevel.emoji}
                progress={fanProgress}
                points={fanPoints}
                nextLabel={fanNext?.label ?? null}
                t={t}
              />
              <p className="pt-1 text-[11px] leading-snug text-muted-foreground">
                {t("heat_panel_explanation")}
              </p>
              <button
                type="button"
                onClick={sendPulse}
                disabled={cooldown > 0}
                className="heat-gradient-team shadow-team-identity mt-2 w-full rounded-full px-4 py-2.5 text-sm font-black text-primary-foreground active:scale-95 disabled:opacity-60 disabled:active:scale-100"
              >
                {cooldown > 0
                  ? t("heat_panel_cooldown").replace("{s}", String(cooldown))
                  : boostLabel}
              </button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">{t("heat_panel_no_window")}</p>
              <HeatRivalryMini highlight={supportedCode} />
              <HeatContributions t={t} />
              <FanLevelStrip
                level={fanLevel.label}
                emoji={fanLevel.emoji}
                progress={fanProgress}
                points={fanPoints}
                nextLabel={fanNext?.label ?? null}
                t={t}
              />
              <p className="text-[11px] leading-snug text-muted-foreground">{t("heat_panel_explanation")}</p>
              <button
                type="button"
                onClick={sendPulse}
                disabled={cooldown > 0}
                className="w-full rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-bold text-primary disabled:opacity-60"
              >
                {cooldown > 0
                  ? t("heat_panel_cooldown").replace("{s}", String(cooldown))
                  : boostLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    )}
    {firstUseOpen && (
      <div
        className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
        onClick={dismissFirstUse}
        role="dialog"
        aria-label={t("fan_power_first_title")}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm rounded-t-3xl border border-primary/40 bg-background p-5 shadow-2xl sm:rounded-2xl"
        >
          <div className="mb-2 text-center text-base font-black tracking-wide text-primary">
            {t("fan_power_first_title")}
          </div>
          <p className="mb-3 text-center text-sm leading-snug text-foreground/90">
            {t("fan_power_first_body")}
          </p>
          <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
            {t("fan_power_first_strengthens")}
          </div>
          <ul className="mb-4 space-y-1 text-[13px] font-bold">
            <li>✓ {t("heat_panel_contributes_rivalry")}</li>
            <li>✓ {t("heat_panel_contributes_storm")}</li>
            <li>✓ {t("heat_panel_contributes_ranking")}</li>
            <li>✓ {t("fan_pride_label")}</li>
          </ul>
          <button
            type="button"
            onClick={dismissFirstUse}
            className="heat-gradient-team shadow-team-identity w-full rounded-full px-4 py-2.5 text-sm font-black text-primary-foreground active:scale-95"
          >
            {t("fan_power_first_cta")}
          </button>
        </div>
      </div>
    )}
    </>
  );
}

function HeatBar({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums">{value}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-secondary/60">
        <div
          className="heat-gradient-team h-full rounded-full transition-all"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

function HeatContributions({ t }: { t: (k: string) => string }) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/40 p-2">
      <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
        {t("heat_panel_contributes_title")}
      </div>
      <div className="flex flex-wrap gap-1">
        {[
          "heat_panel_contributes_storm",
          "heat_panel_contributes_rivalry",
          "heat_panel_contributes_momentum",
          "heat_panel_contributes_ranking",
        ].map((k) => (
          <span key={k} className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-bold text-primary">
            {t(k)}
          </span>
        ))}
      </div>
    </div>
  );
}

function FanLevelStrip({
  level, emoji, progress, points, nextLabel, t,
}: {
  level: string;
  emoji: string;
  progress: number;
  points: number;
  nextLabel: string | null;
  t: (k: string) => string;
}) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/40 p-2">
      <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <span>{t("fan_power_level")}</span>
        <span className="tabular-nums">{points} pts</span>
      </div>
      <div className="mb-1 flex items-center justify-between text-[12px] font-black">
        <span>{emoji} {level}</span>
        {nextLabel && <span className="text-[10px] font-bold text-muted-foreground">→ {nextLabel}</span>}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary/60">
        <div
          className="heat-gradient-team h-full rounded-full transition-all"
          style={{ width: `${Math.round(progress)}%` }}
        />
      </div>
    </div>
  );
}
